// A video's sound tracks: the ones its source carries, and the ones added beside it.

import { api, type UploadProgress } from '../client'
import type { components } from '../schema'

export type AudioTrackEntry = components['schemas']['AudioTrackEntry']
export type AudioTrackPlayback = components['schemas']['AudioTrackPlayback']
export type UploadAudioTrackRequest = components['schemas']['UploadAudioTrackRequest']
export type UpdateAudioTrackRequest = components['schemas']['UpdateAudioTrackRequest']

function tracksPath(assetId: string): string {
  return `/assets/${encodeURIComponent(assetId)}/audio-tracks`
}

/**
 * A video's tracks at its current generation, in track order; the default carries a flag rather
 * than coming first. 404 for an asset that is not a video.
 */
export function listAssetAudioTracks(assetId: string, signal?: AbortSignal): Promise<AudioTrackEntry[]> {
  return api.get<AudioTrackEntry[]>(tracksPath(assetId), undefined, signal)
}

/**
 * Add a track of one's own, at the next free number, and report how far its bytes have gone up.
 * Its encoding is queued with it: the entry answers `playback: 'preparing'`, and the player offers
 * the track once a rendition has landed.
 */
export function uploadAssetAudioTrack(
  assetId: string,
  request: UploadAudioTrackRequest,
  file: File,
  onProgress: (progress: UploadProgress) => void = () => {},
  signal?: AbortSignal,
): Promise<AudioTrackEntry> {
  const form = new FormData()
  form.append('metadata', JSON.stringify(request))
  form.append('file', file)
  return api.postFormWithProgress<AudioTrackEntry>(tracksPath(assetId), form, onProgress, signal)
}

/** Say what a track is. Making one the default takes the flag off the others. */
export function updateAssetAudioTrack(
  assetId: string,
  trackId: string,
  request: UpdateAudioTrackRequest,
): Promise<AudioTrackEntry> {
  return api.patch<AudioTrackEntry>(`${tracksPath(assetId)}/${encodeURIComponent(trackId)}`, request)
}

/** Take a track away, with the audio renditions encoded from it. */
export function deleteAssetAudioTrack(assetId: string, trackId: string): Promise<void> {
  return api.del<void>(`${tracksPath(assetId)}/${encodeURIComponent(trackId)}`)
}
